import { useDispatch } from 'react-redux';

import SignupForm from '@/components/SignupForm';
import { useSelector } from '@/redux/helper';

import { signUpCopy } from '../copy';
import { withResumeState } from '../core/resumeState';
import speedyFlowSlice from '../redux/slice';

const { advanceSpeedyFlow, setSpeedyFlowEmail } = speedyFlowSlice.actions;

/**
 * The sign-up step. The form is the same component the `/signup` page renders — including the
 * 13-or-older and terms gate, which must not be skipped just because this one lives in a modal.
 *
 * The only difference is where success goes: the page navigates, this dispatches an event and lets
 * the machine decide whether the visitor still owes us a verification, a payment, or nothing.
 */
const SignUpStep = () => {
  const dispatch = useDispatch();
  const productChoice = useSelector((state) => state.modals.speedyFlow.machine?.productChoice);
  const persona = useSelector((state) => state.modals.speedyFlow.machine?.persona);

  /**
   * Where the emailed link should bring them back to: this page, because this is where the flow is.
   *
   * Not the saved post-auth destination, which is what the form would otherwise use. That records
   * the last page they were trying to *reach*, and it deliberately survives a move to `/home` —
   * so signing up in a modal on `/home` sent the verification link to a habitat they had browsed
   * earlier and walked away from.
   *
   * Read at render rather than at submit because the flow closes on navigation, so the page cannot
   * change underneath it. A product already chosen rides along in the same `sf` form the rest of
   * the flow uses, so the link resumes the purchase rather than restarting it.
   */
  const here = `${window.location.pathname}${window.location.search}`;
  const returnPath = productChoice?.productId ? withResumeState(here, productChoice) : here;

  return (
    <div className="flex flex-col p-6" data-component="speedy-step" data-step="sign-up">
      <h2 className="preflight preflight-h2 mb-6 text-center text-2xl font-bold text-forest-dark">{signUpCopy.title}</h2>
      <SignupForm
        emailVerificationPath={returnPath}
        // Answered on the opening step, before there was an account to answer against. It travels
        // with the registration, so the account is created with it rather than acquiring it a
        // moment later.
        persona={persona}
        submitLabel={signUpCopy.submit}
        onSignedUp={({ email }) => {
          dispatch(setSpeedyFlowEmail(email));
          dispatch(advanceSpeedyFlow({ type: 'signedUpByEmail' }));
        }}
        footer={
          <button
            type="button"
            className="preflight preflight-button mt-2 text-sm text-grey-3 underline hover:text-grey-2"
            onClick={() => dispatch(advanceSpeedyFlow({ type: 'choseLogIn' }))}
          >
            I already have an account
          </button>
        }
      />
    </div>
  );
};

export default SignUpStep;
